import isAfter from 'date-fns/isAfter'
import { Notification } from 'src/dto'

import { remoteStorageService } from '@/utils/axios/remoteStorage'
import dateUtils from '@/utils/DateUtils'

type NotificationType = keyof Pick<
  typeof remoteStorageService,
  'PUBLIC_WORKBOOK_OPEN_NOTIFICATION' | 'ESSAY_WORKSHEET_OPEN_NOTIFICATION'
>

const now = new Date()

export const releaseNotificationUtils = {
  initNotificationData: async (notificationType: NotificationType, expiredDate: Date) => {
    const EXPIRED_DATE = expiredDate.getTime()

    if (
      !remoteStorageService[notificationType] ||
      !remoteStorageService[notificationType].startDate
    ) {
      const initialParams = {
        startDate: Date.now(),
        expiredDate: EXPIRED_DATE,
        isRead: false,
      }
      await remoteStorageService.patch(notificationType, initialParams)
      return initialParams
    } else {
      const result = await remoteStorageService.get(notificationType)
      return result?.[notificationType]
    }
  },

  createPublicWorkbookOpenNotification: ({
    startDate,
    expiredDate,
    isRead,
  }: typeof remoteStorageService.PUBLIC_WORKBOOK_OPEN_NOTIFICATION) => {
    return new Notification(
      {
        partitionKey: 'publicWorkbookOpenNotification',
        sortKey: startDate,
        createDateTime: dateUtils.formatLocalISO(new Date(startDate)),
        data: {
          payload: {
            title: '시중교재 업데이트',
            type: 'MATHFLAT_MESSAGE',
            state: 'GROUP_REPORT_UPDATE',
            message:
              '선생님의 시간을 아끼고, 시중교재 수업을 더 풍부하게! 선생님이 필요로 했던 시중교재가 대거 추가되었어요.',
            case: 'GROUP_REPORT_CREATE',
          },
          cta: [
            {
              action: 'CLIENT_NOTIFICATION_PUBLIC_WORKBOOK_OPEN',
              data: null,
            },
            {
              action: 'CLIENT_NOTIFICATION_PUBLIC_WORKBOOK_OPEN',
              text: '자세히 보기',
              data: null,
            },
          ],
        },
        read: isAfter(now, new Date(expiredDate)) ? true : isRead,
        expireDateTime: dateUtils.formatLocalISO(new Date(expiredDate)),
      },
      'CLIENT',
    )
  },
  createEssayNotification: ({
    startDate,
    expiredDate,
    isRead,
  }: typeof remoteStorageService.ESSAY_WORKSHEET_OPEN_NOTIFICATION) => {
    return new Notification(
      {
        partitionKey: 'essayWorksheetOpenNotification',
        sortKey: startDate,
        createDateTime: dateUtils.formatLocalISO(new Date(startDate)),
        data: {
          payload: {
            title: '서술형 콘텐츠 출시 안내',
            type: 'MATHFLAT_MESSAGE',
            state: 'ESSAY_REPORT_UPDATE',
            message: `내신 대비 서술형 콘텐츠가 새롭게 제공됩니다.
            - 지원 대상 : 중등 1‧2‧3학년
            - 고등 공통수학Ⅱ
            매쓰플랫이 선생님의 수업 준비에 서술형까지 든든하게 지원합니다.`,
            case: 'GROUP_REPORT_CREATE',
          },
          cta: [
            {
              action: 'CLIENT_NOTIFICATION_ESSAY_WORKSHEET_OPEN',
              data: null,
            },
            {
              action: 'CLIENT_NOTIFICATION_ESSAY_WORKSHEET_OPEN',
              text: '자세히 보기',
              data: null,
            },
          ],
        },
        read: isAfter(now, new Date(expiredDate)) ? true : isRead,
        expireDateTime: dateUtils.formatLocalISO(new Date(expiredDate)),
      },
      'CLIENT',
    )
  },

  getIsUnRead: (notificationType: NotificationType) => {
    const remoteStorageData = remoteStorageService[notificationType]

    if (!remoteStorageData) {
      return false
    }

    const expiredDate = new Date(remoteStorageData.expiredDate)
    const isExpired = isAfter(now, expiredDate)
    if (isExpired) {
      return false
    }
    return !remoteStorageData.isRead
  },

  readNotification: async (notificationType: NotificationType) => {
    const data = remoteStorageService[notificationType]
    await remoteStorageService.patch(notificationType, {
      ...data,
      isRead: true,
    })
  },
}
